import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { css, cx } from '@linaria/atomic';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, CheckboxField, SearchField, Text, useControlledField } from '@frontend/design-system';
import { TreeLevelStyle, TreeNode } from './tree-checklist.types';
import { TreeNodeRow } from './tree-node-row';
import { filterTreeBySearch, findLabel, getSelectableLeafIds, toggleNode } from './tree-selection.utils';
import { useFieldWidthBreakpoint } from './use-field-width-breakpoint';

// The dropdown opens at least this wide regardless of the (possibly compact) field, so group/location
// labels and the "X of N" badges stay readable even when the collapsed field is narrow.
const POPOVER_MIN_WIDTH = 380;

export type TreeChecklistDropdownProps = {
  collapsedLabel?: (selectedCount: number) => string;
  emptyText?: string;
  isError?: boolean;
  isLoading?: boolean;
  isRequired?: boolean;
  label: string;
  levelStyles?: TreeLevelStyle[];
  nodes: TreeNode[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  readOnly?: boolean;
  showApplyButton?: boolean;
  styles?: {
    container?: CSSProperties;
    field?: CSSProperties;
    label?: CSSProperties;
    popover?: CSSProperties;
  };
  trackingIdPrefix: string;
  value: string[];
  width?: number | string;
};

export const TreeChecklistDropdown = ({
  collapsedLabel,
  emptyText,
  isError: apiError = false,
  isLoading = false,
  isRequired = false,
  label,
  levelStyles = [],
  nodes,
  onChange,
  placeholder,
  readOnly = false,
  showApplyButton = true,
  styles: slotStyles,
  trackingIdPrefix,
  value,
  width,
}: TreeChecklistDropdownProps) => {
  const { t } = useTranslation('messages');
  const [isOpen, setIsOpen] = useState(false);
  const [staged, setStaged] = useState<string[]>(value);
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [touched, setTouched] = useState(false);
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  // Variant follows the OPEN dropdown's width (which honors POPOVER_MIN_WIDTH), not the collapsed field —
  // so a compact field still gets the readable "large" layout with "X of N" badges.
  const variant = useFieldWidthBreakpoint(popoverRef);

  useEffect(() => {
    if (!isOpen) setStaged(value);
  }, [value, isOpen]);

  const commit = (next: string[]) => {
    setStaged(next);
    if (!showApplyButton) onChange(next);
  };

  const filtered = useMemo(() => filterTreeBySearch(nodes, search), [nodes, search]);
  // Scope Select All / "all selected" to the currently-visible (filtered) selectable leaves, so during a
  // search it reflects the results the user sees rather than the whole tree.
  const selectableSet = useMemo(() => new Set(getSelectableLeafIds(filtered)), [filtered]);
  const totalLeaves = selectableSet.size;
  const stagedSet = useMemo(() => new Set(staged), [staged]);
  const isError = isRequired && touched && value.length === 0;

  const floatingNodeId = useFloatingNodeId();
  const { refs, context, floatingStyles } = useFloating<HTMLDivElement>({
    open: isOpen,
    onOpenChange: (next) => {
      setIsOpen(next);
      if (!next) {
        setSearch('');
        setTouched(true);
        if (showApplyButton) setStaged(value);
      }
    },
    placement: 'bottom-start',
    nodeId: floatingNodeId,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(3),
      flip(),
      shift({ padding: 8 }),
      // Set width only. Deriving maxHeight from availableHeight here fights flip() under autoUpdate
      // (placement → availableHeight → maxHeight → size change → re-flip …) and locks the UI when the
      // list is tall (all groups expanded). Height is capped by CSS (styles.popover maxHeight + scroll).
      size({
        apply({ elements, rects }) {
          Object.assign(elements.floating.style, {
            // Decoupled from the field: at least POPOVER_MIN_WIDTH, or the field width if it's wider.
            width: `${Math.max(rects.reference.width, POPOVER_MIN_WIDTH)}px`,
          });
        },
      }),
    ],
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { enabled: !readOnly }),
    useRole(context, { role: 'listbox' }),
    useDismiss(context),
  ]);

  const searchFieldProps = useControlledField({ onChange: setSearch, type: 'text', value: search });
  const selectedInTree = staged.filter((id) => selectableSet.has(id)).length;
  const allSelected = totalLeaves > 0 && selectedInTree === totalLeaves;
  // Staged ids not in the current selectable set: attached-but-inaccessible locations, and (during a
  // search) selectable ids hidden by the filter. Preserve them across Select All / clear so neither drops them.
  const nonSelectableStaged = staged.filter((id) => !selectableSet.has(id));
  const selectAllProps = useControlledField({
    // Binary: when every selectable leaf is selected, clear; otherwise select all. Keyed on allSelected
    // (not "any selected") so clicking an unchecked box always selects rather than wiping a partial set.
    onChange: () => commit(allSelected ? nonSelectableStaged : [...nonSelectableStaged, ...selectableSet]),
    type: 'checkbox',
    value: allSelected,
  });

  const collapsedText = useMemo(() => {
    if (!value.length) return placeholder ?? t('No Selection');
    if (value.length === 1) {
      const name = findLabel(nodes, value[0]);
      return name ?? (collapsedLabel ? collapsedLabel(1) : t('{{number}} selected', { number: 1 }));
    }
    return collapsedLabel ? collapsedLabel(value.length) : t('{{number}} selected', { number: value.length });
  }, [value, nodes, collapsedLabel, placeholder, t]);

  // The tree is flattened into ONE array, so every row is a sibling. A group id can equal a location id
  // (self-grouped single locations), so `node.id` alone is not unique across levels — key by tree path
  // instead, otherwise React duplicates/omits rows on expand/collapse.
  const renderRows = (list: TreeNode[], depth = 0, parentKey = ''): JSX.Element[] =>
    list.flatMap((node, index) => {
      const rowKey = `${parentKey}${depth}:${index}:${node.id}`;
      const hasChildren = !!node.children?.length;
      const expanded = expandedIds.has(node.id) || !!search;
      const rows = [
        <TreeNodeRow
          key={rowKey}
          node={node}
          levelStyle={levelStyles[depth] ?? {}}
          selected={stagedSet}
          expanded={expanded}
          hasChildren={hasChildren}
          variant={variant}
          onToggleSelect={(n) => commit(toggleNode(n, staged))}
          onToggleExpand={(n) =>
            setExpandedIds((prev) => {
              const nextSet = new Set(prev);
              nextSet.has(n.id) ? nextSet.delete(n.id) : nextSet.add(n.id);
              return nextSet;
            })
          }
          trackingIdPrefix={trackingIdPrefix}
        />,
      ];
      if (hasChildren && expanded) rows.push(...renderRows(node.children!, depth + 1, `${rowKey}/`));
      return rows;
    });

  return (
    <div className={styles.container} style={{ width, ...slotStyles?.container }}>
      <div
        ref={(el) => {
          refs.setReference(el);
          fieldRef.current = el;
        }}
        {...getReferenceProps()}
        className={cx(
          styles.field,
          isOpen && styles.fieldActive,
          !isOpen && isError && styles.fieldError,
          readOnly && styles.fieldReadOnly
        )}
        style={slotStyles?.field}
        data-trackingid={`${trackingIdPrefix}-field`}
        tabIndex={readOnly ? -1 : 0}
        aria-required={isRequired}
        aria-invalid={isError}
      >
        <span className={styles.floatingLabel} style={slotStyles?.label}>
          {label}
        </span>
        <span className={styles.value}>{collapsedText}</span>
        <span className={cx(styles.caret, isOpen && styles.caretOpen)} aria-hidden>
          <Icon name='caret-down' size={16} color='subdued' />
        </span>
      </div>

      <FloatingNode id={floatingNodeId}>
        {isOpen && (
          <FloatingPortal>
            <FloatingFocusManager context={context} initialFocus={-1}>
              <div
                ref={(el) => {
                  refs.setFloating(el);
                  popoverRef.current = el;
                }}
                {...getFloatingProps()}
                className={styles.popover}
                style={{ ...floatingStyles, ...slotStyles?.popover }}
              >
                {!isLoading && !apiError && (
                  <>
                    <div className={styles.searchRow}>
                      <SearchField
                        {...searchFieldProps}
                        className={styles.searchInput}
                        name={`${trackingIdPrefix}-search`}
                        placeholder={t('Search All')}
                      />
                    </div>
                    <div className={styles.toolbar}>
                      <CheckboxField
                        {...selectAllProps}
                        className={styles.selectAll}
                        disabled={readOnly}
                        label={t('Select All')}
                        labelPlacement='right'
                        name={`${trackingIdPrefix}-select-all`}
                        trackingId={`${trackingIdPrefix}-select-all`}
                      />
                    </div>
                  </>
                )}

                <div className={styles.scroll}>
                  {isLoading && <Text className={styles.state}>{t('Loading…')}</Text>}
                  {apiError && !isLoading && <Text className={styles.state}>{t("Couldn't load locations.")}</Text>}
                  {!isLoading && !apiError && !filtered.length && (
                    <Text className={styles.state}>{emptyText ?? t('No results found')}</Text>
                  )}
                  {!isLoading && !apiError && renderRows(filtered)}
                </div>

                {showApplyButton && (
                  <Button
                    className={styles.apply}
                    disabled={readOnly}
                    onClick={() => {
                      onChange(staged);
                      setSearch('');
                      setIsOpen(false);
                    }}
                    trackingId={`${trackingIdPrefix}-apply`}
                  >
                    {t('Apply')}
                  </Button>
                )}
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </FloatingNode>
    </div>
  );
};

const styles = {
  container: css`
    position: relative;
    background: ${theme.colors.white};
  `,
  field: css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    gap: ${theme.spacing(1)};
    height: 40px;
    padding: ${theme.spacing(0, 2)};
    border: 2px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.small};
    background: inherit;
    cursor: pointer;
  `,
  fieldActive: css`
    border-color: ${theme.colors.primary50};
  `,
  fieldError: css`
    border-color: ${theme.colors.critical50};
  `,
  fieldReadOnly: css`
    cursor: not-allowed;
    opacity: 0.7;
  `,
  floatingLabel: css`
    position: absolute;
    top: -8px;
    left: ${theme.spacing(1.5)};
    padding: ${theme.spacing(0, 0.5)};
    background: inherit;
    font-size: ${theme.font.size.small};
    line-height: 16px;
    color: ${theme.colorsV2.black(60)};
  `,
  value: css`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: ${theme.font.size.large};
    line-height: 24px;
    color: ${theme.colorsV2.black(85)};
  `,
  caret: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: transform 200ms;
    color: ${theme.colorsV2.black(60)};
  `,
  caretOpen: css`
    transform: rotate(180deg);
  `,
  popover: css`
    display: flex;
    flex-direction: column;
    max-height: 400px;
    background: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
    box-shadow: ${theme.shadows.floating};
    z-index: ${theme.zIndex.popover};
    overflow: hidden;
  `,
  toolbar: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(1)};
    min-height: 48px;
    flex-shrink: 0;
    padding: ${theme.spacing(1, 2)};
    border-bottom: 2px solid ${theme.colorsV2.black(10)};
  `,
  selectAll: css`
    margin: 0;
    flex-shrink: 0;
  `,
  searchRow: css`
    flex-shrink: 0;
    padding: ${theme.spacing(1.5, 2)};
  `,
  searchInput: css`
    width: 100%;
  `,
  scroll: css`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  `,
  state: css`
    padding: ${theme.spacing(2)};
    text-align: center;
    color: ${theme.colorsV2.black(60)};
  `,
  apply: css`
    flex-shrink: 0;
    margin: ${theme.spacing(1, 2)};
    width: calc(100% - ${theme.spacing(4)});
  `,
};
